import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  XMarkIcon, 
  ArrowUturnLeftIcon,
  ArrowUturnRightIcon,
  EyeIcon,
  CodeBracketIcon,
  PencilIcon,
  ArrowDownTrayIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';
import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import { Document } from '@tvia-ai/shared';
import { removeCodeBlockWrapper, detectRTL } from '../../utils/downloadUtils';
import DocumentPreviewPage from './DocumentPreviewPage';
import DocumentAIChat from './DocumentAIChat';

interface FreeHTMLEditorProps {
  document: Document;
  lawsuitId?: string;
  isOpen: boolean;
  onClose: () => void;
  onSave: (updatedDocument: Document) => void;
  createNewVersion?: boolean;
}

const FreeHTMLEditor: React.FC<FreeHTMLEditorProps> = ({
  document,
  lawsuitId,
  isOpen,
  onClose,
  onSave,
  createNewVersion = true
}) => {
  const { t, i18n } = useTranslation();
  const [content, setContent] = useState(document.content || '');
  const [editorMode, setEditorMode] = useState<'rich' | 'html' | 'preview'>('preview');
  const [undoStack, setUndoStack] = useState<string[]>([]);
  const [redoStack, setRedoStack] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showAIChat, setShowAIChat] = useState(false);
  const editorRef = useRef<HTMLDivElement>(null);

  // Helper function to add code block wrapper
  const addCodeBlockWrapper = (content: string): string => {
    if (!content.startsWith('```html')) {
      return `\`\`\`html\n${content}\n\`\`\``;
    }
    return content;
  };

  // Determine direction based on language primarily, with content fallback
  const isRTL = i18n.language === 'he' || i18n.language === 'ar' || detectRTL(content);

  // Initialize content when document changes
  useEffect(() => {
    const cleanContent = removeCodeBlockWrapper(document.content || '');
    setContent(cleanContent);
    setUndoStack([cleanContent]);
    setRedoStack([]);
  }, [document.content]);

  // Get current content from the active editor
  const getCurrentContent = (): string => {
    if (editorMode === 'rich' && editorRef.current) {
      return editorRef.current.innerHTML;
    }
    return content;
  };

  // Handle content changes with undo/redo support - only called on save/mode switch
  const handleContentChange = (newContent: string) => {
    if (newContent !== content) {
      setUndoStack(prev => [...prev, content]);
      setRedoStack([]);
      setContent(newContent);
    }
  };

  const handleUndo = () => {
    if (undoStack.length > 1) {
      const previousContent = undoStack[undoStack.length - 2];
      setRedoStack(prev => [content, ...prev]);
      setUndoStack(prev => prev.slice(0, -1));
      setContent(previousContent);
    }
  };

  const handleRedo = () => {
    if (redoStack.length > 0) {
      const nextContent = redoStack[0];
      setUndoStack(prev => [...prev, content]);
      setRedoStack(prev => prev.slice(1));
      setContent(nextContent);
    }
  };

  // Handle mode switching - sync content when switching
  const handleModeSwitch = (newMode: 'rich' | 'html' | 'preview') => {
    // Get current content before switching
    const currentContent = getCurrentContent();
    
    // Update content state
    handleContentChange(currentContent);
    
    // Switch mode
    setEditorMode(newMode);
  };

  // Save document mutation
  const saveDocumentMutation = useMutation({
    mutationFn: async (updatedContent: string) => {
      const contentToSave = addCodeBlockWrapper(updatedContent);
      const response = await axios.put(`/api/documents/${document.id}`, {
        content: contentToSave,
        originalDocumentId: document.id,
        createNewVersion: createNewVersion
      });
      return response.data;
    },
    onSuccess: (data) => {
      const updatedDocument: Document = {
        ...document,
        ...data.data.document,
        content: content
      };
      onSave(updatedDocument);
      onClose();
    },
  });

  const handleSave = async () => {
    // Get the latest content from the active editor
    const currentContent = getCurrentContent();
    
    // Update state first
    handleContentChange(currentContent);
    
    setIsSaving(true);
    try {
      await saveDocumentMutation.mutateAsync(currentContent);
    } catch (error) {
      console.error('Failed to save document:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const showDocumentPreview = () => {
    setShowPreview(true);
  };

  const handleAIDocumentUpdate = (newContent: string, changesSummary?: any) => {
    // Update content and add to undo stack
    handleContentChange(newContent);
    
    // Update rich editor if it's active
    if (editorMode === 'rich' && editorRef.current) {
      editorRef.current.innerHTML = newContent;
    }
    
    // Log the changes summary for debugging (can be expanded for UI feedback)
    if (changesSummary) {
      console.log('AI Changes Applied:', changesSummary);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 flex items-start sm:items-center justify-center p-2 sm:p-4">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl h-full sm:h-auto sm:max-h-[90vh] flex flex-col sm:my-8">
          {/* Header */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 sm:p-6 border-b border-gray-200 gap-4">
            <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4 min-w-0 flex-1">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900 truncate">
                {t('editor.title', 'Edit Document')}: {document.originalName}
              </h2>
              <div className="flex items-center space-x-2 flex-shrink-0">
                <button
                  onClick={handleUndo}
                  disabled={undoStack.length <= 1}
                  className="p-2 text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  title={t('editor.undo', 'Undo')}
                >
                  <ArrowUturnLeftIcon className="h-5 w-5" />
                </button>
                <button
                  onClick={handleRedo}
                  disabled={redoStack.length === 0}
                  className="p-2 text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  title={t('editor.redo', 'Redo')}
                >
                  <ArrowUturnRightIcon className="h-5 w-5" />
                </button>
                <div className="hidden sm:block h-6 w-px bg-gray-300" />
                <div className="flex items-center space-x-1 bg-gray-100 rounded-lg p-1">
                  <button
                    onClick={() => handleModeSwitch('rich')}
                    className={`p-2 rounded min-h-[40px] min-w-[40px] flex items-center justify-center ${editorMode === 'rich' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                    title={t('editor.richTextMode', 'Rich Text Mode')}
                  >
                    <PencilIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleModeSwitch('html')}
                    className={`p-2 rounded min-h-[40px] min-w-[40px] flex items-center justify-center ${editorMode === 'html' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                    title={t('editor.htmlMode', 'HTML Mode')}
                  >
                    <CodeBracketIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleModeSwitch('preview')}
                    className={`p-2 rounded min-h-[40px] min-w-[40px] flex items-center justify-center ${editorMode === 'preview' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                    title={t('editor.previewMode', 'Preview Mode')}
                  >
                    <EyeIcon className="h-4 w-4" />
                  </button>
                </div>

                <button
                  onClick={() => setShowAIChat(!showAIChat)}
                  className={`px-2 sm:px-4 py-2 text-sm border rounded-lg flex items-center min-h-[44px] transition-all ${
                    showAIChat 
                      ? 'bg-blue-600 text-white border-blue-600' 
                      : 'text-blue-600 hover:text-blue-800 border-blue-300 hover:bg-blue-50'
                  }`}
                  title={t('editor.aiAssistant', 'AI Assistant')}
                >
                  <SparklesIcon className="h-4 w-4 sm:mr-1" />
                  <span className="hidden sm:inline">{t('editor.aiAssistant', 'AI Assistant')}</span>
                </button>

                <button
                  onClick={showDocumentPreview}
                  className="px-2 sm:px-4 py-2 text-sm text-blue-600 hover:text-blue-800 border border-blue-300 rounded-lg flex items-center min-h-[44px] transition-all hover:bg-blue-50"
                  title={t('editor.previewAndDownload', 'Preview & Download')}
                >
                  <ArrowDownTrayIcon className="h-4 w-4 sm:mr-1" />
                  <span className="hidden sm:inline">{t('editor.previewDownload', 'Preview & Download')}</span>
                </button>
              </div>
            </div>
            <div className="flex items-center space-x-2 flex-shrink-0">
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="px-4 py-3 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg min-h-[44px] transition-all"
              >
                {isSaving ? t('editor.saving', 'Saving...') : t('common.save', 'Save')}
              </button>
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg min-h-[44px] min-w-[44px] flex items-center justify-center"
                aria-label={t('common.close')}
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Editor Content */}
          <div className={`flex-1 flex flex-col lg:flex-row overflow-hidden ${isRTL ? 'lg:flex-row-reverse' : 'lg:flex-row'} min-h-0`}>
            {/* Main Editor Panel */}
            <div className={`flex-1 flex flex-col min-h-0 ${showAIChat ? (isRTL ? 'lg:border-l border-gray-200' : 'lg:border-r border-gray-200') : ''}`}>
              {editorMode === 'preview' ? (
              /* Preview Mode */
              <div className="flex-1 overflow-auto bg-gray-50">
                <div className="p-6">
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <div
                      className={`prose prose-sm max-w-none ${isRTL ? 'text-right' : 'text-left'}`}
                      dir={isRTL ? 'rtl' : 'ltr'}
                      style={{
                        textAlign: isRTL ? 'right' : 'left',
                        direction: isRTL ? 'rtl' : 'ltr'
                      }}
                      dangerouslySetInnerHTML={{ __html: content }}
                    />
                  </div>
                </div>
              </div>
            ) : editorMode === 'rich' ? (
              /* Rich Text Editor Mode */
              <div className="flex-1 flex flex-col min-h-0">
                <div className="p-4 bg-gray-50 border-b border-gray-200 flex-shrink-0">
                  <p className="text-sm text-gray-600">
                    {t('editor.richTextInstructions', 'Use the rich text editor to format your document visually. Switch to HTML mode to see the underlying code.')}
                  </p>
                </div>
                <div className="flex-1 relative bg-white overflow-auto p-4 min-h-0">
                  <div
                    ref={editorRef}
                    contentEditable
                    suppressContentEditableWarning={true}
                    className={`w-full p-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[300px] text-base ${isRTL ? 'text-right' : 'text-left'}`}
                    dir={isRTL ? 'rtl' : 'ltr'}
                    style={{
                      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", sans-serif',
                      fontSize: '14px',
                      lineHeight: '1.6',
                      minHeight: '400px',
                      textAlign: isRTL ? 'right' : 'left',
                      direction: isRTL ? 'rtl' : 'ltr'
                    }}
                    dangerouslySetInnerHTML={{ __html: removeCodeBlockWrapper(content) }}
                  />
                </div>
              </div>
            ) : (
              /* HTML Code Editor Mode */
              <div className="flex-1 flex flex-col min-h-0">
                <div className="flex-1 relative min-h-0">
                  <textarea
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    className={`absolute inset-0 w-full h-full p-4 font-mono text-base border-0 resize-none focus:ring-0 focus:outline-none ${isRTL ? 'text-right' : 'text-left'}`}
                    dir={isRTL ? 'rtl' : 'ltr'}
                    style={{
                      textAlign: isRTL ? 'right' : 'left',
                      direction: isRTL ? 'rtl' : 'ltr'
                    }}
                    placeholder={t('editor.placeholder', 'Enter HTML content...')}
                    spellCheck={false}
                  />
                </div>
              </div>
            )}
            </div>

            {/* AI Chat Panel */}
            {showAIChat && lawsuitId && (
              <div className="w-full lg:w-96 flex flex-col bg-white border-t lg:border-t-0 lg:border-l border-gray-200">
                <DocumentAIChat
                  lawsuitId={lawsuitId}
                  documentContent={getCurrentContent()}
                  onDocumentUpdate={handleAIDocumentUpdate}
                  onClose={() => setShowAIChat(false)}
                  language={i18n.language || 'en'}
                />
              </div>
            )}
            {showAIChat && !lawsuitId && (
              <div className="w-full lg:w-96 flex flex-col bg-white border-t lg:border-t-0 lg:border-l border-gray-200">
                <div className="p-4 bg-yellow-50 border-b border-yellow-200">
                  <div className="flex items-center space-x-2">
                    <div className="text-yellow-600">⚠️</div>
                    <p className="text-sm text-yellow-800">
                      {t('aiChat.noLawsuitContext', 'AI Assistant requires lawsuit context to function properly.')}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-500">
              {t('editor.wordCount', 'Characters')}: {editorMode === 'rich' && editorRef.current ? editorRef.current.textContent?.length || 0 : content.length}
            </div>
            <div className="text-sm text-gray-500">
              {content !== document.content && (
                <span className="text-orange-600">
                  {t('editor.unsavedChanges', 'Unsaved changes')}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Document Preview Page */}
      <DocumentPreviewPage
        document={{
          content: getCurrentContent(),
          originalName: document.originalName
        }}
        isOpen={showPreview}
        onClose={() => setShowPreview(false)}
      />
    </>
  );
};

export default FreeHTMLEditor;